import { DataFrame } from '@lib/mobx/dataframe';
import { assert } from '@lib/utils/assert';
import { toError } from '@lib/utils/toError';
import * as Sentry from '@sentry/react';
import { action, computed, makeObservable, observable, onBecomeObserved } from 'mobx';

// Published by ijw-fyi/internal.changelog: one day per line, newest first, images relative to the origin.
// Ported from the chat frontend (monorepo.vibedit `SideBar/ChangelogStore.ts`); keep the parser in sync.
const CHANGELOG_ORIGIN = 'https://changelog.starzero.ai';
const CHANGELOG_URL = `${CHANGELOG_ORIGIN}/changelog.v1.jsonl`;
// The CDN answers a Range header with the whole file, so the head read streams and cancels instead.
// 30 KB is about four weeks of days at the current entry density.
const HEAD_BYTES = 30_000;
const CHANGELOG_READ_HASH_KEY = 'CHANGELOG_READ_HASH';

const ENTRY_KINDS = ['new', 'change', 'fix'] as const;
export type ChangelogEntryKind = (typeof ENTRY_KINDS)[number];
export type ChangelogEntry = { kind: ChangelogEntryKind; text: string; image?: string };
export type ChangelogDay = { date: string; entries: ChangelogEntry[] };

const ENTRY_PREFIX: Record<ChangelogEntryKind, string> = {
  new: `<span style='color: orangered'>**_NEW:_**</span> `,
  fix: `<span style='color: #4dabf7'>**_FIX:_**</span> `,
  change: '',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;
const isKind = (value: unknown): value is ChangelogEntryKind =>
  ENTRY_KINDS.some((kind) => kind === value);

function parseDay(line: string): ChangelogDay {
  const day: unknown = JSON.parse(line);
  assert(
    isRecord(day) &&
      typeof day.date === 'string' &&
      /^\d{4}-\d{2}-\d{2}$/.test(day.date) &&
      Array.isArray(day.entries),
    `changelog line is not a day: ${line.slice(0, 80)}`,
  );
  const entries = day.entries.map((entry: unknown): ChangelogEntry => {
    assert(
      isRecord(entry) && typeof entry.text === 'string' && entry.text,
      `changelog entry has no text on ${day.date}`,
    );
    assert(isKind(entry.kind), `unknown changelog entry kind on ${day.date}: ${entry.kind}`);
    const parsed: ChangelogEntry = { kind: entry.kind, text: entry.text };
    if (entry.image !== undefined) {
      assert(typeof entry.image === 'string', `changelog entry image is not a path on ${day.date}`);
      parsed.image = entry.image;
    }
    return parsed;
  });
  return { date: day.date, entries };
}

function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash = hash & hash; // keep it a 32-bit integer
  }
  return hash.toString(36);
}

function concatBytes(chunks: Uint8Array[], total: number): Uint8Array {
  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}

type ChangelogData = { days: ChangelogDay[]; complete: boolean };

/**
 * The published changelog, read at runtime. The first read stops after `HEAD_BYTES` and drops the
 * line it cut through; `loadAll()` reads to the end. The unread marker is the newest day's content,
 * persisted when the user closes the popover, so an entry appended to today's day shows the dot
 * again. A failed read keeps whatever loaded before it, so a failed "Load all" leaves the head on
 * screen next to the error.
 */
export class ChangelogStore extends DataFrame<ChangelogData> {
  override keepDataOnFetchError = true;

  @observable.ref
  private readHash = localStorage.getItem(CHANGELOG_READ_HASH_KEY);

  private wantAll = false;

  constructor() {
    super();
    makeObservable(this);
    // The read starts when the dot first renders; a later remount hits the cached data.
    onBecomeObserved(this, 'data', () => this.load());
  }

  @computed
  get complete(): boolean {
    return this.data?.complete ?? false;
  }

  @computed
  get markdown(): string | null {
    if (!this.data) return null;
    const days = this.data.days.map((day) => {
      const entries = day.entries.map((entry) => {
        const image = entry.image ? `\n\n  ![](${CHANGELOG_ORIGIN}/${entry.image})` : '';
        return `- ${ENTRY_PREFIX[entry.kind]}${entry.text}${image}`;
      });
      return `#### ${day.date}\n\n${entries.join('\n')}`;
    });
    return `### Changelog\n\n${days.join('\n\n')}`;
  }

  @computed
  private get newestHash(): string | null {
    const newest = this.data?.days[0];
    return newest ? hashString(JSON.stringify(newest)) : null;
  }

  @computed
  get hasUnread(): boolean {
    return this.newestHash !== null && this.newestHash !== this.readHash;
  }

  /** Called when the popover closes; a close before the feed loaded has nothing to mark. */
  @action
  markRead() {
    if (this.newestHash === null) return;
    localStorage.setItem(CHANGELOG_READ_HASH_KEY, this.newestHash);
    this.readHash = this.newestHash;
  }

  loadAll() {
    this.wantAll = true;
    this.load(true);
  }

  /** Repeats the last requested read, head or full. */
  retry() {
    this.load(true);
  }

  private load(update = false) {
    this.get(update).catch((e: unknown) => Sentry.captureException(toError(e)));
  }

  protected async fetch(signal: AbortSignal): Promise<ChangelogData> {
    const response = await fetch(CHANGELOG_URL, { signal });
    assert(response.ok, `changelog request failed: ${response.status} ${CHANGELOG_URL}`);
    assert(response.body, 'changelog response has no body');

    const cap = this.wantAll ? Infinity : HEAD_BYTES;
    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    let complete = true;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      if (received >= cap) {
        complete = false;
        await reader.cancel();
        break;
      }
    }

    const text = new TextDecoder().decode(concatBytes(chunks, received));
    if (complete) {
      assert(text.endsWith('\n'), 'changelog file does not end with a newline');
    }
    // Drop the line the cap cut through; on a complete read this only strips the trailing newline.
    const lastNewline = text.lastIndexOf('\n');
    assert(lastNewline >= 0, 'changelog head holds no complete day');
    const days = text.slice(0, lastNewline).split('\n').map(parseDay);
    return { days, complete };
  }
}

export const changelogStore = new ChangelogStore();
